import { useMemo, useState } from 'react';
import { useAuthStore } from '../../stores/auth';
import {
  BodyText,
  Button,
  MetricCard,
  METRIC_READ_CAPTIONS,
  PageHeader,
} from '@aaimplatform/ui';
import type { ColumnDef } from '@aaimplatform/ui';
import type { components } from '@aaimplatform/institutional-types/ucc-fee-reconciliation';
import {
  RefreshCw as RefreshIcon,
} from 'lucide-react';
import { usePayments, useReconciliationReport } from '../../hooks';
import {
  ABSENT,
  formatCount,
  formatDate,
  formatMoney,
  formatMoneyCompact,
} from '../../utils/display-format';
import {
  CodeBadge,
  FEE_PAYMENT_STATUS_LABEL,
  FEE_PAYMENT_STATUS_VARIANT,
  metricCellClass,
  metricGridClass,
  MetricSkeletons,
  shortId,
  UccTable,
  UccTenantScope,
} from './ucc-display';

type FeePayment = components['schemas']['FeePayment'];
type ReconciliationSummary = components['schemas']['ReconciliationSummary'];

const DEFAULT_PAGE_SIZE = 20;

/** Page sizes the rows-per-page control offers. */
const PAGE_SIZE_OPTIONS = [20, 50, 100];

/** Days of history the reconciliation summary covers. */
export const RECONCILIATION_WINDOW_DAYS = 90;

/** Integer cents from the fee-reconciliation spec to the dollars the shared formatter takes. */
function centsToDollars(cents: number): number {
  return cents / 100;
}

const SUMMARY_CARDS: ReadonlyArray<{ label: string; render: (s: ReconciliationSummary) => string }> = [
  { label: 'Payments', render: (s) => formatCount(s.paymentCount) },
  { label: 'Reconciled', render: (s) => formatCount(s.reconciledCount) },
  { label: 'Pending', render: (s) => formatCount(s.pendingCount) },
  { label: 'Refunded', render: (s) => formatCount(s.refundedCount) },
  { label: 'Total paid', render: (s) => formatMoneyCompact(centsToDollars(s.totalPaidCents)) },
];

function usePaymentColumns(): ColumnDef<FeePayment, unknown>[] {
  return useMemo<ColumnDef<FeePayment, unknown>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'Payment ID',
        cell: ({ row }) => <span className="font-mono text-sm">{shortId(row.original.id)}</span>,
      },
      {
        accessorKey: 'filingId',
        header: 'Filing',
        cell: ({ row }) => <span className="font-mono text-sm">{shortId(row.original.filingId)}</span>,
      },
      { accessorKey: 'jurisdictionCode', header: 'Jurisdiction' },
      {
        accessorKey: 'amountCents',
        header: 'Amount',
        cell: ({ row }) => (
          <span className="text-sm font-mono tabular-nums text-primary">
            {formatMoney(centsToDollars(row.original.amountCents))}
          </span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <CodeBadge code={row.original.status} labels={FEE_PAYMENT_STATUS_LABEL} variants={FEE_PAYMENT_STATUS_VARIANT} />
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Recorded',
        cell: ({ row }) => <span className="text-sm text-secondary">{formatDate(row.original.createdAt)}</span>,
      },
    ],
    [],
  );
}

export function UCCFeesPage() {
  const tenantId = useAuthStore(s => s.user?.tenantId) ?? '';
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  // Fixed once per mount. Computed on every render, the window's millisecond
  // bounds changed each time and every render keyed a new report query.
  const [reportWindow] = useState(() => {
    const now = Date.now();
    return {
      fromDate: new Date(now - RECONCILIATION_WINDOW_DAYS * 24 * 60 * 60 * 1000).toISOString(),
      toDate: new Date(now).toISOString(),
    };
  });

  const payments = usePayments({ tenantId, page, limit: pageSize });
  const report = useReconciliationReport({ tenantId, ...reportWindow });
  const columns = usePaymentColumns();

  const rows = payments.data?.data ?? [];
  const totalCount = payments.data?.count ?? 0;
  const summary = report.data?.summary;
  const cardCount = SUMMARY_CARDS.length;
  const summaryCaption = report.error ? METRIC_READ_CAPTIONS.failed : !summary ? METRIC_READ_CAPTIONS.notReported : undefined;
  const isRefreshing = payments.isFetching || report.isFetching;

  return (
    <div>
      <PageHeader
        description={`Filing fee payments and reconciliation. Summary figures cover the last ${RECONCILIATION_WINDOW_DAYS} days.`}
        actions={
          <Button
            variant="secondary"
            onClick={() => { payments.refetch(); report.refetch(); }}
            disabled={isRefreshing || !tenantId}
          >
            <RefreshIcon className={`mr-2 ${isRefreshing ? 'animate-spin' : ''}`} size={16} />
            Refresh
          </Button>
        }
      />

      <UccTenantScope tenantId={tenantId} />

      {tenantId && (
        <>
          <section aria-label="Reconciliation summary" className="mb-6">
            <div className={metricGridClass(cardCount)}>
              {report.isLoading ? (
                <MetricSkeletons count={cardCount} />
              ) : (
                SUMMARY_CARDS.map((card, i) => (
                  <MetricCard
                    key={card.label}
                    label={card.label}
                    value={summary && !summaryCaption ? card.render(summary) : ABSENT}
                    caption={summaryCaption}
                    className={metricCellClass(i, cardCount)}
                  />
                ))
              )}
            </div>
            {report.error && (
              <BodyText variant="ui" className="text-sm text-error">
                The reconciliation report could not be read: {report.error.message}
              </BodyText>
            )}
          </section>

          <UccTable<FeePayment>
            columns={columns}
            rows={rows}
            isLoading={payments.isLoading}
            error={payments.error}
            errorTitle="Could not load fee payments"
            onRetry={() => payments.refetch()}
            empty={{
              title: 'No fee payments recorded for this tenant',
              description: 'A payment appears here once a filing fee is recorded against a filing submission.',
            }}
            getRowId={(row) => row.id}
            pagination={{
              page: page - 1,
              pageSize,
              total: totalCount,
              onPageChange: (index) => setPage(index + 1),
              onPageSizeChange: (size) => { setPageSize(size); setPage(1); },
            }}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
          />
        </>
      )}
    </div>
  );
}
